import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Calendar, Tag } from 'lucide-react';

export default function AdminSubscribers() {
  const { apiClient } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchSubs = useCallback(async () => {
    try { const { data } = await apiClient.get('/api/admin/subscribers'); setSubs(data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const filtered = filter === 'all' ? subs : subs.filter((s) => s.subscriber_type === filter);
  const parentCount = subs.filter((s) => s.subscriber_type === 'parent').length;
  const clinicianCount = subs.filter((s) => s.subscriber_type === 'clinician').length;

  return (
    <div data-testid="admin-subscribers-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Email Subscribers</h1>
          <p className="text-white/50 text-sm">{subs.length} total &middot; {parentCount} parents &middot; {clinicianCount} clinicians</p>
        </div>
        <div className="flex gap-2">
          {['all', 'parent', 'clinician'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${filter === f ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30' : 'bg-white/5 text-white/40 border border-white/10 hover:text-white/70'}`}
              data-testid={`filter-${f}`}>
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40">No subscribers found</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left text-white/40 font-medium p-4">Email</th>
                <th className="text-left text-white/40 font-medium p-4">Name</th>
                <th className="text-left text-white/40 font-medium p-4">Type</th>
                <th className="text-left text-white/40 font-medium p-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/3 transition-colors" data-testid={`admin-sub-${s.id}`}>
                  <td className="p-4 text-white font-semibold flex items-center gap-2"><Mail size={14} className="text-teal-400" /> {s.email}</td>
                  <td className="p-4 text-white/60">{s.name || '—'}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${s.subscriber_type === 'parent' ? 'bg-blue-500/15 text-blue-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                      <Tag size={10} /> {s.subscriber_type}
                    </span>
                  </td>
                  <td className="p-4 text-white/40 flex items-center gap-1"><Calendar size={12} /> {new Date(s.subscribed_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
